import { env } from './env'
import { showToast } from './toast'
import type { EventRecord } from '@romain130492/topchopsticks'

/**
 * Sharing an event from a web page that may or may not be inside WeChat.
 *
 * ---------------------------------------------------------------------------
 * CAPABILITY DETECTION, NOT USER-AGENT SNIFFING.
 *
 * An earlier version refused to call `navigator.share` whenever the UA said
 * `MicroMessenger`. That was wrong on principle and wrong in effect: the UA
 * string does not tell you whether the Web Share API works, and WeChat's iOS
 * WebView often DOES implement it — opening the iOS system share sheet, which
 * has 发送给微信朋友 in it. Blanket-disabling threw away the exact behaviour
 * we wanted, on every version where it would have worked.
 *
 * So the order is simply:
 *
 *   1. `navigator.share` exists and accepts the payload?  Use it. Everywhere,
 *      WeChat included. The iOS sheet reads the page's Open Graph tags — which
 *      server.mjs now renders per event — so the card comes out rich with no
 *      Official Account involved.
 *   2. It threw something that is not AbortError, or was never there?  Then
 *      this browser genuinely cannot share: copy the link, and inside WeChat
 *      point at ··· , which is the only share affordance that exists there.
 *
 * The fallback stays because it is still needed on WeChat Android, where the
 * API is usually absent. It is a fallback now, not a policy.
 * ---------------------------------------------------------------------------
 */

export const isInWeChat = () => /micromessenger/i.test(navigator.userAgent)

export const eventShareUrl = (event: EventRecord) =>
  `${env.publicOrigin || window.location.origin}/event/${event.id}`

/**
 * Is this event the page we are actually on?
 *
 * It decides what we can honestly tell someone inside WeChat. On the event's
 * own page, ··· shares this event — "tap ··· to share" is true. On the feed,
 * ··· would share the FEED, so saying that would send people to the wrong
 * page; there, copying the link is the only correct answer.
 *
 * Derived from the URL rather than passed in as a prop, so a new caller cannot
 * get it wrong by forgetting.
 */
const isCurrentPage = (event: EventRecord) =>
  window.location.pathname.replace(/\/$/, '') === `/event/${event.id}`

interface WeixinBridge {
  updateAppMessageShareData?: (options: Record<string, unknown>) => void
  updateTimelineShareData?: (options: Record<string, unknown>) => void
}

/**
 * Tell WeChat what THIS page's card should look like. Called whenever the
 * visible event changes, because WeChat remembers the last values set.
 *
 * Returns false when it did nothing — no Official Account, not inside WeChat,
 * or the SDK never loaded.
 */
export function setWeChatShareCard(options: { title: string; desc: string; link: string; imgUrl: string }) {
  if (!isInWeChat() || !env.wechatOaAppId) return false
  const wx = (window as unknown as { wx?: WeixinBridge }).wx
  if (!wx?.updateAppMessageShareData) return false
  wx.updateAppMessageShareData({ ...options })
  wx.updateTimelineShareData?.({ title: options.title, link: options.link, imgUrl: options.imgUrl })
  return true
}

/** Best-effort clipboard write. `execCommand` covers older WeChat WebViews. */
async function copyLink(url: string): Promise<boolean> {
  try {
    // Requires a secure context. Fine in production, absent over plain http.
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url)
      return true
    }
  } catch { /* fall through */ }

  try {
    const field = document.createElement('textarea')
    field.value = url
    field.setAttribute('readonly', '')
    field.style.cssText = 'position:absolute;left:-9999px;top:0;'
    document.body.appendChild(field)
    field.select()
    field.setSelectionRange(0, url.length)   // iOS ignores select() alone
    const ok = document.execCommand('copy')
    document.body.removeChild(field)
    return ok
  } catch {
    return false
  }
}

export async function shareEvent(
  event: EventRecord,
  texts: { title: string; desc: string; copied: string; hint: string }
) {
  const url = eventShareUrl(event)
  const image = event.cover_image_url || event.pictures?.[0]?.sizes?.original || ''

  // Pre-set the WeChat card when an Official Account is configured. Synchronous
  // and a no-op otherwise — deliberately BEFORE the share call and with no
  // `await` between here and it, because the Web Share API needs the user
  // activation from the tap and awaiting anything first can consume it.
  if (isInWeChat() && isCurrentPage(event)) {
    setWeChatShareCard({ title: texts.title, desc: texts.desc, link: url, imgUrl: image })
  }

  // ---- 1. The native share sheet. Tried EVERYWHERE, WeChat included. ----
  const nav = navigator as Navigator & {
    share?: (data: ShareData) => Promise<void>
    canShare?: (data: ShareData) => boolean
  }
  const payload: ShareData = { title: texts.title, text: texts.desc, url }

  // Say which tier ran. "The share button does nothing" is impossible to
  // diagnose remotely otherwise — the three outcomes (OS sheet, clipboard,
  // WeChat hint) look identical from the outside when one of them silently
  // fails, and the browser gives no signal either way.
  console.info('[tc][share]', {
    inWeChat: isInWeChat(),
    hasWebShare: typeof nav.share === 'function',
    canShare: nav.canShare ? nav.canShare(payload) : 'n/a',
    secureContext: typeof window !== 'undefined' ? window.isSecureContext : 'n/a',
    url
  })

  if (typeof nav.share === 'function' && (!nav.canShare || nav.canShare(payload))) {
    try {
      await nav.share(payload)
      return
    } catch (error) {
      // AbortError means the person dismissed the sheet — that is a completed
      // interaction, not a failure, so do not then shove a toast at them.
      // Anything else means this browser could not actually share; fall
      // through and give them something that works.
      if ((error as DOMException)?.name === 'AbortError') return
    }
  }

  // ---- 2. No usable share sheet: clipboard, plus guidance inside WeChat. ----
  const copied = await copyLink(url)

  // Inside WeChat, ··· shares the CURRENT page — so that hint is only true on
  // the event's own page. On the feed it would send people to the list.
  if (isInWeChat() && isCurrentPage(event)) showToast({ title: texts.hint, icon: 'none' })
  else if (copied) showToast({ title: texts.copied, icon: 'success' })
  else window.prompt(texts.copied, url)
}
